unit SimpleDashboard;

interface

uses
  System.Generics.Collections,
  JS,
  XData.Web.Client,
  Sys.Return.Types,
  SMX.XData.Utils,
  Simple.Dashboard.Return.Types,
  Simple.Dashboard.Bulk.Types;

type

  TSimpleDashboard = class
  private
    FWebClient: TXDataWebClient;
    FItemList: TDictionary<string, TDashboardResponse>;
    FBranchStructureCache: string;
    FBranchStructureLoaded: Boolean;
    FLatestTransactionDate: string;
    FLatestTransactionDateLoaded: Boolean;
    FDefaultCurrencySymbol: string;
    FDefaultCurrencyLoaded: Boolean;
    FUserBranchesCache: TDictionary<Integer, string>;
    procedure SetWebClient(const Value: TXDataWebClient);
    function BuildItemCacheKey(const AKey, AParams: string): string;
    function CopyDashboardResponse(const ASource: TDashboardResponse): TDashboardResponse;
    function BuildBulkRequestJSON(const AParams: string;
      ATemplates: TList<TDashboardItemTemplate>): string;
    function SummaryFieldsToJSON(AFields: TArray<TSummaryArray>): string;
    function ExtractXDataStringValue(ARetval: TXDataClientResponse): string;

  public
    constructor Create(AWebClient: TXDataWebClient);
    destructor Destroy; override;
    procedure ClearDashboardCache;
    procedure ClearBranchMetadataCache;
    procedure SetCachedCurrencySymbol(const ASymbol: string);
    function HasCachedCurrencySymbol: Boolean;

    [ASync]
    function GetDashboardItem(const AKey: string; const AParams: string; ATemplate: TDashboardItemTemplate): TDashboardResponse; async;

    [ASync]
    function GetDashboardSummaryData(const AKey: string; AFields: TArray<TSummaryArray>): string; async;
    [ASync]
    function GetBranchStructure: string; async;
    [ASync]
    function GetLatestTransactionDate: string; async;
    [ASync]
    function GetDefaultCurrencySymbol: string; async;
    function DefaultCurrencySymbol: string;
    [ASync]
    function GetDashboardItemsBulk(const AParams: string;
      ATemplates: TList<TDashboardItemTemplate>): TList<TDashboardBulkItem>; async;
    [ASync]
    function GetDashboardItemTemplates(const AUserId: integer): TList<TDashboardItemTemplate>; async;
    [ASync]
    function GetUserConfig(const AUserId: integer): TUserConfig; async;
    [ASync]
    function SetUserConfig(AUserID: integer; ARecordId: integer; AUserConfig: string): Boolean; async;
    [ASync]
    function GetTenantDashboardItems(const ATenantId: string; const AUserId: integer): TList<TDashboardItemTitle>; async;
    [ASync]
    procedure SaveUserDashboardItemTitles(AUserId: integer; AUserDashboardItemTitles: TList<TDashboardItemTitle>); async;

    property WebClient: TXDataWebClient read FWebClient write SetWebClient;

    [ASync]
    function GetUserBranchesForUser(const AUserId: integer): string; async;

  end;

implementation

uses
  System.SysUtils;

const
  SVC_GET_ITEM = 'ISimpleDashboardService.GetDashboardItem';
  SVC_GET_SUMMARY_DATA = 'ISimpleDashboardService.GetDashboardSummaryData';
  SVC_GET_BRANCH_STRUCTURE = 'ISimpleDashboardService.GetBranchStructure';
  SVC_GET_LATEST_TRANSACTION_DATE = 'ISimpleDashboardService.GetLatestTransactionDate';
  SVC_GET_DEFAULT_CURRENCY_SYMBOL = 'ISimpleDashboardService.GetDefaultCurrencySymbol';
  SVC_GET_ITEMS_BULK = 'ISimpleDashboardService.GetDashboardItemsBulk';
  SVC_GET_DASHBOARD_TEMPLATES = 'IDashboardManagementService.GetDashboardItemsForUser';

constructor TSimpleDashboard.Create(AWebClient: TXDataWebClient);
begin
  FWebClient := AWebClient;
  FItemList := TDictionary<string, TDashboardResponse>.Create;
  FUserBranchesCache := TDictionary<Integer, string>.Create;
  FBranchStructureLoaded := False;
  FLatestTransactionDate := '';
  FLatestTransactionDateLoaded := False;
  FDefaultCurrencyLoaded := False;
  FDefaultCurrencySymbol := '';
end;

destructor TSimpleDashboard.Destroy;
begin
  FUserBranchesCache.Free;
  FItemList.Free;
  inherited;
end;

procedure TSimpleDashboard.ClearDashboardCache;
begin
  FItemList.Clear;
end;

procedure TSimpleDashboard.ClearBranchMetadataCache;
begin
  FBranchStructureCache := '';
  FBranchStructureLoaded := False;
  FLatestTransactionDate := '';
  FLatestTransactionDateLoaded := False;
  FDefaultCurrencySymbol := '';
  FDefaultCurrencyLoaded := False;
  FUserBranchesCache.Clear;
end;

procedure TSimpleDashboard.SetCachedCurrencySymbol(const ASymbol: string);
var
  lSymbol: string;
begin
  lSymbol := Trim(ASymbol);
  if lSymbol = '' then
    lSymbol := #163;
  FDefaultCurrencySymbol := lSymbol;
  FDefaultCurrencyLoaded := True;
end;

function TSimpleDashboard.HasCachedCurrencySymbol: Boolean;
begin
  Result := FDefaultCurrencyLoaded;
end;

function TSimpleDashboard.BuildItemCacheKey(const AKey, AParams: string): string;
begin
  Result := AKey + '|' + AParams;
end;

function TSimpleDashboard.CopyDashboardResponse(const ASource: TDashboardResponse): TDashboardResponse;
begin
  Result := TDashboardResponse.Create;
  Result.ChartJson := ASource.GetChartPayload;
  if Trim(Result.ChartJson) = '' then
    Result.ChartJson := ASource.ChartJson;
  Result.Key := ASource.Key;
  Result.Description := ASource.Description;
  Result.Hint := ASource.Hint;
  Result.ItemType := ASource.ItemType;
  Result.Scale := ASource.Scale;
  Result.Expires := ASource.Expires;
  Result.Title := ASource.Title;
  Result.Configuration := ASource.Configuration;
  Result.SummaryType := ASource.SummaryType;
  Result.SummaryPresent := ASource.SummaryPresent;
  Result.SummaryData := ASource.GetSummaryPayload;
  if Trim(Result.SummaryData) = '' then
    Result.SummaryData := ASource.SummaryData;
  Result.DrillDownPresent := ASource.DrillDownPresent;
  Result.DrillDownSQL := ASource.DrillDownSQL;
  Result.ChartType := ASource.ChartType;
  Result.ChartTypeFixed := ASource.ChartTypeFixed;
  Result.FullSummaryAvailable := ASource.FullSummaryAvailable;
end;

function TSimpleDashboard.GetBranchStructure: string;
var
  lRetval: TXDataClientResponse;
begin
  if FBranchStructureLoaded then
    Exit(FBranchStructureCache);

  lRetval := Await(TXDataClientResponse, FWebClient.RawInvokeAsync(SVC_GET_BRANCH_STRUCTURE, []));
  Result := String(TJSObject(lRetval.Result)['value']);
  FBranchStructureCache := Result;
  FBranchStructureLoaded := True;
end;

function TSimpleDashboard.BuildBulkRequestJSON(const AParams: string;
  ATemplates: TList<TDashboardItemTemplate>): string;
begin
  asm
    var templates = [];
    var count = ATemplates.GetCount();
    for (var i = 0; i < count; i++) {
      var t = ATemplates.GetItem(i);
      templates.push({
        Id: t.Id,
        DashboardItemType: t.DashboardItemType,
        Title: t.Title,
        Configuration: t.Configuration,
        SQLSummary: t.SQLSummary,
        SQLChart: t.SQLChart,
        ChartType: t.ChartType,
        ChartTypeFixed: t.ChartTypeFixed,
        Preferences: t.Preferences
      });
    }
    var params = {};
    if (AParams) {
      try {
        params = JSON.parse(AParams);
      } catch (e) {
        params = {};
      }
    }
    Result = JSON.stringify({ params: params, templates: templates });
  end;
end;

function TSimpleDashboard.SummaryFieldsToJSON(AFields: TArray<TSummaryArray>): string;
var
  lArr: TJSArray;
  lObj: TJSObject;
  lIdx: Integer;
begin
  lArr := TJSArray.new;
  for lIdx := 0 to Length(AFields) - 1 do
  begin
    lObj := TJSObject.new;
    lObj.Properties['SearchName'] := AFields[lIdx].SearchName;
    lObj.Properties['ToField'] := AFields[lIdx].ToField;
    lObj.Properties['AggregateFunction'] := AFields[lIdx].AggregateFunction;
    lObj.Properties['AggregateStore'] := AFields[lIdx].AggregateStore;
    lObj.Properties['DisplayName'] := AFields[lIdx].DisplayName;
    lObj.Properties['FieldType'] := AFields[lIdx].FieldType;
    lArr.Push(lObj);
  end;
  Result := string(TJSJSON.stringify(lArr));
end;

function TSimpleDashboard.ExtractXDataStringValue(ARetval: TXDataClientResponse): string;
var
  lResultObj: TJSObject;
begin
  Result := '';
  if not Assigned(ARetval) or not Assigned(ARetval.Result) then
    Exit;

  if JS.isString(ARetval.Result) then
    Result := string(ARetval.Result)
  else
  begin
    lResultObj := TJSObject(ARetval.Result);
    if Assigned(lResultObj) then
    begin
      if lResultObj.Properties['value'] <> nil then
        Result := string(lResultObj.Properties['value'])
      else
        Result := string(ARetval.Result);
    end;
  end;
end;

function TSimpleDashboard.GetLatestTransactionDate: string;
var
  lRetval: TXDataClientResponse;
begin
  if FLatestTransactionDateLoaded then
    Exit(FLatestTransactionDate);

  Result := '';
  try
    lRetval := Await(TXDataClientResponse, FWebClient.RawInvokeAsync(SVC_GET_LATEST_TRANSACTION_DATE, []));
    Result := ExtractXDataStringValue(lRetval);
  except
    Result := '';
  end;

  if Result <> '' then
  begin
    FLatestTransactionDate := Result;
    FLatestTransactionDateLoaded := True;
  end;
end;

function TSimpleDashboard.GetDefaultCurrencySymbol: string;
var
  lRetval: TXDataClientResponse;
begin
  if FDefaultCurrencyLoaded then
    Exit(FDefaultCurrencySymbol);

  Result := '';
  try
    lRetval := Await(TXDataClientResponse,
      FWebClient.RawInvokeAsync(SVC_GET_DEFAULT_CURRENCY_SYMBOL, []));
    Result := Trim(ExtractXDataStringValue(lRetval));
  except
    Result := '';
  end;

  if Result = '' then
    Result := #163;

  FDefaultCurrencySymbol := Result;
  FDefaultCurrencyLoaded := True;
end;

function TSimpleDashboard.DefaultCurrencySymbol: string;
begin
  if FDefaultCurrencyLoaded then
    Result := FDefaultCurrencySymbol
  else
    Result := #163;
end;

function TSimpleDashboard.GetDashboardItemsBulk(const AParams: string;
  ATemplates: TList<TDashboardItemTemplate>): TList<TDashboardBulkItem>;
var
  lRetval: TXDataClientResponse;
  lBulkItems: TList<TDashboardBulkItem>;
  lIdx: integer;
  lList: TJSArray;
  lBulkItem: TDashboardBulkItem;
begin
  lRetval := Await(TXDataClientResponse,
    FWebClient.RawInvokeAsync(SVC_GET_ITEMS_BULK,
      [BuildBulkRequestJSON(AParams, ATemplates)]));

  lBulkItems := TList<TDashboardBulkItem>.Create;
  lList := TsmxXDataUtils.AsArray(lRetval);
  for lIdx := 0 to lList.Length - 1 do
  begin
    lBulkItem := TDashboardBulkItem.Create(JS.toObject(lList[lIdx]));
    lBulkItems.Add(lBulkItem);
    if (not FDefaultCurrencyLoaded) and (Trim(lBulkItem.CurrencySymbol) <> '') then
      SetCachedCurrencySymbol(lBulkItem.CurrencySymbol);
  end;

  Result := lBulkItems;
end;

function TSimpleDashboard.GetDashboardItem(const AKey: string; const AParams: string; ATemplate: TDashboardItemTemplate): TDashboardResponse;
var
  lRetval: TXDataClientResponse;
  lObject: JS.TJSObject;
  lCacheKey: string;
  lCached: TDashboardResponse;
begin
  lCacheKey := BuildItemCacheKey(AKey, AParams);
  if FItemList.TryGetValue(lCacheKey, lCached) then
    Exit(CopyDashboardResponse(lCached));

  lRetval := Await(TXDataClientResponse, FWebClient.RawInvokeAsync(SVC_GET_ITEM, [AKey, AParams, ATemplate]));
  lObject := UnwrapXDataResultObject(lRetval.ResultAsObject);

  Result := TDashboardResponse.Create;
  Result.Assign(lObject);
  if (Trim(Result.GetChartPayload) <> '') or (Trim(Result.GetSummaryPayload) <> '') then
    FItemList.AddOrSetValue(lCacheKey, CopyDashboardResponse(Result));
end;

function TSimpleDashboard.GetDashboardItemTemplates(const AUserId: integer): TList<TDashboardItemTemplate>;
var
  lRetval: TXDataClientResponse;
  lTemplates: TList<TDashboardItemTemplate>;
  lIdx: integer;
  lList: TJSArray;
begin
  lRetval := Await(TXDataClientResponse, FWebClient.RawInvokeAsync(SVC_GET_DASHBOARD_TEMPLATES, [AUserId]));

  lTemplates := TList<TDashboardItemTemplate>.Create;

  lList := TsmxXDataUtils.AsArray(lRetval);
  for lIdx := 0 to lList.Length - 1 do
    lTemplates.Add(TDashboardItemTemplate.Create(JS.toObject(lList[lIdx])));

  Result := lTemplates;
end;

function TSimpleDashboard.GetDashboardSummaryData(const AKey: string; AFields: TArray<TSummaryArray>): string;
var
  lRetval: TXDataClientResponse;
  lFieldsJSON: string;
begin
  Result := '[]';
  if Trim(AKey) = '' then
    Exit;

  lFieldsJSON := SummaryFieldsToJSON(AFields);

  try
    lRetval := Await(TXDataClientResponse, FWebClient.RawInvokeAsync(SVC_GET_SUMMARY_DATA, [AKey, lFieldsJSON]));
    Result := ExtractXDataStringValue(lRetval);
  except
    on E: Exception do
    begin
      asm
        console.log('GetDashboardSummaryData failed');
      end;
      Result := '[]';
    end;
  end;

  if Trim(Result) = '' then
    Result := '[]';
end;

function TSimpleDashboard.GetTenantDashboardItems(const ATenantId: string; const AUserId: integer): TList<TDashboardItemTitle>;
const
  svc_name = 'IDashboardManagementService.GetTenantDashboardItems';

var
  lRetval: TXDataClientResponse;
  lDashboardItemTitles: TList<TDashboardItemTitle>;
  lIdx: integer;
  lList: TJSArray;
begin
  lRetval := Await(TXDataClientResponse, FWebClient.RawInvokeAsync(svc_name, [ATenantId, AUserId]));

  lDashboardItemTitles := TList<TDashboardItemTitle>.Create;

  lList := TsmxXDataUtils.AsArray(lRetval);
  for lIdx := 0 to lList.Length - 1 do
    lDashboardItemTitles.Add(TDashboardItemTitle.Create(JS.toObject(lList[lIdx])));

  Result := lDashboardItemTitles;
end;

procedure TSimpleDashboard.SetWebClient(const Value: TXDataWebClient);
begin
  FWebClient := Value;
end;

procedure TSimpleDashboard.SaveUserDashboardItemTitles(AUserId: integer; AUserDashboardItemTitles: TList<TDashboardItemTitle>);
const
  svc_delete = 'IDashboardManagementService.DeleteUserDashboardItems';
  svc_save = 'IDashboardManagementService.SaveUserDashboardItemTitle';

var
  lRetval: TXDataClientResponse;
  lDashboardItemTitle: TDashboardItemTitle;
  lIdx: integer;
begin
  if not Assigned(AUserDashboardItemTitles) then
    Exit;

  lRetval := Await(TXDataClientResponse, FWebClient.RawInvokeAsync(svc_delete, [AUserId]));

  for lIdx := 0 to AUserDashboardItemTitles.Count - 1 do
  begin
    lDashboardItemTitle := AUserDashboardItemTitles[lIdx];
    if not lDashboardItemTitle.Selected then
      Continue;
    if lDashboardItemTitle.Id <= 0 then
      Continue;
    lDashboardItemTitle.Selected := True;
    lRetval := Await(TXDataClientResponse, FWebClient.RawInvokeAsync(svc_save, [AUserId, lDashboardItemTitle]));
  end;
end;

function TSimpleDashboard.GetUserConfig(const AUserId: integer): TUserConfig;
const
  svc_user_config = 'IDashboardManagementService.GetUserConfiguration';

var
  lRetval: TXDataClientResponse;
  lObject: JS.TJSObject;
begin
  lRetval := Await(TXDataClientResponse, FWebClient.RawInvokeAsync(svc_user_config, [AUserId]));
  lObject := lRetval.ResultAsObject;
  Result := TUserConfig.Create;
  Result.Assign(lObject);
end;

function TSimpleDashboard.SetUserConfig(AUserID: integer; ARecordId: integer; AUserConfig: string): Boolean;
const
  svc_user_config = 'IDashboardManagementService.SetUserConfiguration';

var
  lRetval: TXDataClientResponse;
begin
  Result := False;
  lRetval := Await(TXDataClientResponse, FWebClient.RawInvokeAsync(svc_user_config, [AUserId, ARecordId, AUserConfig]));
  if not Assigned(lRetval) or not Assigned(lRetval.Result) then
    Exit;

  asm
    var payload = lRetval.Result;
    if (payload === true || payload === false) {
      Result = payload;
    } else if (payload && payload.hasOwnProperty('value')) {
      Result = !!payload.value;
    } else {
      Result = !!payload;
    }
  end;
end;

function TSimpleDashboard.GetUserBranchesForUser(const AUserId: integer): string;
const
  svc_get_branches_for_user = 'IBranchService.GetBranchesForUser';

var
  lRetval: TXDataClientResponse;
begin
  if FUserBranchesCache.TryGetValue(AUserId, Result) then
    Exit;

  lRetval := Await(TXDataClientResponse, FWebClient.RawInvokeAsync(svc_get_branches_for_user, [AUserId]));
  Result := String(TJSObject(lRetval.Result)['value']);
  FUserBranchesCache.AddOrSetValue(AUserId, Result);
end;

end.
